import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, unwrap, type Schemas } from './client';

export type Kiosk = Schemas['KioskDto'];
export type KioskRequest = Schemas['KioskRequest'];
export type KioskDevice = Schemas['KioskDeviceDto'];
export type AttendanceRow = Schemas['AttendanceRow'];
export type TerminalInfo = Schemas['TerminalInfo'];
export type TerminalUserState = Schemas['UserState'];
export type KioskAction = Schemas['ActionRequest']['action'];

const kioskKey = (ws: string) => ['workspace', ws, 'kiosks'] as const;

export function useKiosks(ws: string) {
  return useQuery({
    queryKey: kioskKey(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/kiosks', { params: { path: { workspaceId: ws } } })),
  });
}

export function useSaveKiosk(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: KioskRequest & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/kiosks/{id}', { params: { path: { workspaceId: ws, id } }, body }))
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/kiosks', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: kioskKey(ws) }),
  });
}

export function useDeleteKiosk(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/kiosks/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: kioskKey(ws) }),
  });
}

export function useRegenerateKioskLink(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/kiosks/{id}/regenerate-link', { params: { path: { workspaceId: ws, id } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: kioskKey(ws) }),
  });
}

export function useKioskDevices(ws: string, id: string | null) {
  return useQuery({
    queryKey: [...kioskKey(ws), id, 'devices'],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/kiosks/{id}/devices', { params: { path: { workspaceId: ws, id: id! } } })),
    enabled: !!id,
  });
}

export function useRevokeKioskDevice(ws: string, kioskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      unwrap(
        api.DELETE('/api/v1/workspaces/{workspaceId}/kiosks/{id}/devices/{deviceId}', {
          params: { path: { workspaceId: ws, id: kioskId, deviceId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...kioskKey(ws), kioskId, 'devices'] }),
  });
}

export function useAttendance(ws: string, timezone: string) {
  return useQuery({
    queryKey: ['workspace', ws, 'time-entries', 'attendance', timezone],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/attendance', { params: { path: { workspaceId: ws }, query: { timezone } } })),
    refetchInterval: 30_000,
  });
}

export function useKioskPinStatus(ws: string, enabled: boolean) {
  return useQuery({
    queryKey: ['workspace', ws, 'kiosk-pin'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/kiosk-pin', { params: { path: { workspaceId: ws } } })),
    enabled,
  });
}

export function useSetKioskPin(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pin: string) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/kiosk-pin', { params: { path: { workspaceId: ws } }, body: { pin } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws, 'kiosk-pin'] }),
  });
}

export function useGenerateKioskPin(ws: string) {
  return useMutation({
    mutationFn: ({ userId, length }: { userId: string; length: number }) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/members/{userId}/kiosk-pin', {
          params: { path: { workspaceId: ws, userId }, query: { length } },
        }),
      ),
  });
}

// ------------------------------------------------------------------ Terminal (Geräte-Token statt Login)

const DEVICE_PREFIX = 'ft.kiosk.device.';

export const kioskDevice = {
  get: (linkToken: string) => {
    try {
      return localStorage.getItem(DEVICE_PREFIX + linkToken);
    } catch {
      return null;
    }
  },
  set: (linkToken: string, token: string) => {
    try {
      localStorage.setItem(DEVICE_PREFIX + linkToken, token);
    } catch {
      // Speicher nicht verfügbar – Kopplung gilt nur für diese Sitzung
    }
  },
  clear: (linkToken: string) => {
    try {
      localStorage.removeItem(DEVICE_PREFIX + linkToken);
    } catch {
      // ignorieren
    }
  },
};

const header = (deviceToken: string) => ({ 'X-Kiosk-Token': deviceToken });

export async function pairKiosk(linkToken: string, deviceName: string) {
  return unwrap(api.POST('/api/v1/kiosk/pair', { body: { linkToken, deviceName } }));
}

export function useTerminal(deviceToken: string | null) {
  return useQuery({
    queryKey: ['kiosk-terminal', deviceToken],
    queryFn: () => unwrap(api.GET('/api/v1/kiosk/terminal', { params: { header: header(deviceToken!) } })),
    enabled: !!deviceToken,
    refetchInterval: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 3,
  });
}

export function useKioskUnlock(deviceToken: string) {
  return useMutation({
    mutationFn: (body: { userId: string; pin: string }) =>
      unwrap(api.POST('/api/v1/kiosk/unlock', { params: { header: header(deviceToken) }, body })),
  });
}

export function useKioskAction(deviceToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['ActionRequest']) =>
      unwrap(api.POST('/api/v1/kiosk/action', { params: { header: header(deviceToken) }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kiosk-terminal'] }),
  });
}
